import {Client as ConversationsClient, Conversation, Message, Paginator} from "@twilio/conversations";
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from "@angular/core";
import {CurrentSiteContextService} from "../../../commons/angular/services/current-site-context.service";
import {ChatHttpService} from "../../services/chat-http.service";
import {Subscription} from "rxjs";
import {ChatUserModel} from "../../models/chat-user.model";
import {BringozChatConversationModel} from "../../models/chat-conversation.model";
import {ChatService} from "../../services/chat.service";
import {ChatFacadeService} from "../../state/chat-facade.service";

require('./chat-view.scss');
@Component({
  standalone: false,
  selector: 'chat-view',
  templateUrl: './chat-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatViewComponent implements OnInit, OnDestroy {

  private conversationsClientSubscription: Subscription;
  // private selectConversationSubscription: Subscription;
  public conversationsClient: ConversationsClient;
  public conversations: Conversation[];
  public activeConversation: Conversation;
  public messages: Message[];
  public users: ChatUserModel[];
  public currentUserIdentity: string;

  public conversationsPaginator: Paginator<Conversation>

  public initialized: boolean = false;
  public isDropdownOpen: boolean;

  private siteId: string;

  // Stable handler refs so we can `.off(...)` exactly what we registered.
  // The Twilio client is a shared singleton (BehaviorSubject in ChatService),
  // so we must NOT call removeAllListeners() on it.
  private _boundClient: ConversationsClient | null = null;
  private readonly _onConversationJoined = (conversation: Conversation) => {
    console.info("conversationJoined", conversation);
    this.conversations = [conversation, ...(this.conversations || [])];
    this._checkDetector();
  };
  private readonly _onConversationLeft = (conversation: Conversation) => {
    console.info("conversationLeft");
    this.conversations = [...(this.conversations || []).filter((c) => c !== conversation)];
    this._checkDetector();
  };
  private readonly _onMessageAdded = (_message: Message) => {
    console.info("chat-conversations-list - messageAdded");
  };
  private readonly _onInitialized = () => {
    this.initialized = true;
    console.info("initialized, user:", this.conversationsClient.user.identity);
    this.currentUserIdentity = this.conversationsClient.user.identity;
    this._checkDetector();
  };

  constructor(
    @Inject(CurrentSiteContextService) private currentSiteContextService: CurrentSiteContextService,
    @Inject(ChatHttpService) private chatHttpService: ChatHttpService,
    @Inject(ChatService) private chatService: ChatService,
    @Inject(ChatFacadeService) private chatFacadeService: ChatFacadeService,
    @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.conversationsClientSubscription = this.chatService.conversationsClient
      .subscribe((conversationsClient: ConversationsClient) => {
        this.conversationsClient = conversationsClient;

        this._getSubscribedConversations();
        this._initConversationsEvents();
      });

    // this.selectConversationSubscription = this.chatFacadeService.selectConversation()
    //   .subscribe((conversation: Conversation) => {
    //     this.activeConversation = conversation;
    //   });
  }

  ngOnDestroy(): void {
    this.conversationsClientSubscription?.unsubscribe();
    this._unbindClientEvents();
  }

  onUserSelect(user: ChatUserModel) {
    const chatConversation: BringozChatConversationModel = {
      participants: [user]
    };
    this.chatHttpService.createConversation(this.siteId, chatConversation)
      .subscribe((c: BringozChatConversationModel) => {
        this.conversationsClient.getConversationByUniqueName(c.externalId)
          .then((conversation: Conversation) => {
            this.activeConversation = conversation;
            this._checkDetector();
          })
      })
  }

  onConversationSelect(conversation: Conversation) {
    this.activeConversation = conversation;
  }

  onCloseActiveConversation(conversation?: Conversation) {
    this.activeConversation = undefined;
  }

  onCloseChat() {
    this.chatFacadeService.setIsOpen(false);
  }

  selectConversation(conversation: Conversation, event: Event) {
    event.preventDefault();
    this.activeConversation = conversation;

    conversation.getMessages().then(m => {
      this.messages = m.items;
      this._checkDetector();
      // const mes = this.messages.pop();
      // mes.author
      // this.$scope.$digest();
    })
  }

  deleteConversation(event: Event) {
    event.preventDefault();

    this.activeConversation.delete().then(conversation => {
      this.activeConversation = null;
      this._checkDetector();
    });
  }

  private _checkDetector() {
    this.changeDetectorRef.detectChanges();
  }


  private _getSubscribedConversations() {
    this.conversationsClient.getSubscribedConversations()
      .then((conversationsPaginator: Paginator<Conversation>) => {
        this.conversationsPaginator = conversationsPaginator;

        this.conversations = [...conversationsPaginator.items];

        this._setAllConversationItems();

        this._checkDetector();
      })
  }

  private _setAllConversationItems() {
    if (this.conversationsPaginator.hasNextPage) {
      this.conversationsPaginator.nextPage()
        .then((conversationsPaginator: Paginator<Conversation>) => {
          this.conversationsPaginator = conversationsPaginator;
          this.conversations = this.conversations.concat([...conversationsPaginator.items]);
          this._checkDetector();

          this._setAllConversationItems();
        })
    }
  }

  private _initConversationsEvents() {
    if (!this.conversationsClient) return;

    // Drop any handlers we previously bound on a prior client emission
    // before binding to the (possibly new) current client instance.
    this._unbindClientEvents();
    this._boundClient = this.conversationsClient;

    this.conversationsClient.on("conversationJoined", this._onConversationJoined);
    this.conversationsClient.on("conversationLeft", this._onConversationLeft);
    this.conversationsClient.on("messageAdded", this._onMessageAdded);
    this.conversationsClient.on("initialized", this._onInitialized);
  };

  private _unbindClientEvents() {
    if (!this._boundClient) return;
    this._boundClient.off("conversationJoined", this._onConversationJoined);
    this._boundClient.off("conversationLeft", this._onConversationLeft);
    this._boundClient.off("messageAdded", this._onMessageAdded);
    this._boundClient.off("initialized", this._onInitialized);
    this._boundClient = null;
  }

}
